// src/App.jsx
// Root component — wires providers, router, and lazy-loaded pages

import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import ProtectedRoute   from './components/ui/ProtectedRoute';
import SiteLayout       from './components/SiteLayout';
import { NoIndexRoute } from './components/Seo';
import { trackPageView } from './utils/analytics';

// ── Lazy-loaded pages (code-split automatically) ──────────────────────────
const HomePage        = lazy(() => import('./pages/HomePage'));
const ProductsPage    = lazy(() => import('./pages/ProductsPage'));
const ProductDetail   = lazy(() => import('./pages/ProductDetail'));
const BrandPage       = lazy(() => import('./pages/BrandPage'));
const AppleLanding    = lazy(() => import('./pages/AppleLanding'));
const PolicyPage      = lazy(() => import('./pages/PolicyPage'));
const TryBeforeBuy    = lazy(() => import('./pages/TryBeforeBuy'));
const ExchangePage    = lazy(() => import('./pages/ExchangePage'));
const ComparePage     = lazy(() => import('./pages/ComparePage'));
// const PrebookPage  = lazy(() => import('./pages/PrebookPage'));  // pre-book window over; restore for the next Unpacked
const CartPage        = lazy(() => import('./pages/CartPage'));
const SavedPage       = lazy(() => import('./pages/SavedPage'));
const CheckoutPage    = lazy(() => import('./pages/CheckoutPage'));
const OrderSuccess    = lazy(() => import('./pages/OrderSuccess'));
const OrdersPage      = lazy(() => import('./pages/OrdersPage'));
const OrderDetail     = lazy(() => import('./pages/OrderDetail'));
const ProfilePage     = lazy(() => import('./pages/ProfilePage'));
const LoginPage       = lazy(() => import('./pages/LoginPage'));
const RegisterPage    = lazy(() => import('./pages/RegisterPage'));
const AdminDashboard  = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts   = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders     = lazy(() => import('./pages/admin/AdminOrders'));
const AdminBilling    = lazy(() => import('./pages/admin/AdminBilling'));
const AdminCustomers  = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminReports    = lazy(() => import('./pages/admin/AdminReports'));
const AdminDemos      = lazy(() => import('./pages/admin/AdminDemos'));
const AdminExchange   = lazy(() => import('./pages/admin/AdminExchange'));
const AdminCoupons    = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminPrebookings = lazy(() => import('./pages/admin/AdminPrebookings'));
const NotFound        = lazy(() => import('./pages/NotFound'));

// ── React Query client ────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry:              1,
      staleTime:          60 * 1000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// ── Loading fallback ──────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="empty-state">
      <span><img src="/brand/icon.png" alt="" /></span>
      <p>Loading your experience…</p>
    </div>
  );
}

// Fires a Meta Pixel PageView on client-side navigations. The base pixel in
// index.html already counts the first load, and GA4 tracks route changes via
// Enhanced Measurement — so this is Meta-only and skips the initial render.
function RouteTracker() {
  const { pathname, search } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    trackPageView();
  }, [pathname, search]);
  return null;
}

// ── App ───────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <RouteTracker />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route element={<SiteLayout />}>
                  {/* ── Public routes ── */}
                  <Route path="/"               element={<HomePage />} />
                  <Route path="/products"       element={<ProductsPage />} />
                  <Route path="/products/:id"   element={<ProductDetail />} />
                  <Route path="/brands/apple"   element={<AppleLanding />} />
                  <Route path="/brands/:slug"   element={<BrandPage />} />
                  <Route path="/policies/:slug" element={<PolicyPage />} />
                  <Route path="/try-before-you-buy" element={<TryBeforeBuy />} />
                  <Route path="/exchange"       element={<ExchangePage />} />
                  <Route path="/compare"        element={<ComparePage />} />
                  {/* Pre-booking window over (Fold now on sale) — send to the products. Restore <PrebookPage/> for the next Unpacked. */}
                  <Route path="/prebook"        element={<Navigate to="/products?keyword=Galaxy%20Z%20Fold" replace />} />
                  <Route path="/cart"           element={<NoIndexRoute title="Shopping cart"><CartPage /></NoIndexRoute>} />
                  <Route path="/saved"          element={<NoIndexRoute title="Saved products"><SavedPage /></NoIndexRoute>} />

                  {/* ── Customer protected routes ── */}
                  <Route element={<ProtectedRoute />}>
                    <Route path="/checkout"          element={<NoIndexRoute title="Secure checkout"><CheckoutPage /></NoIndexRoute>} />
                    <Route path="/order-success/:id" element={<NoIndexRoute title="Order confirmed"><OrderSuccess /></NoIndexRoute>} />
                    <Route path="/orders"            element={<NoIndexRoute title="My orders"><OrdersPage /></NoIndexRoute>} />
                    <Route path="/orders/:id"        element={<NoIndexRoute title="Order details"><OrderDetail /></NoIndexRoute>} />
                    <Route path="/profile"           element={<NoIndexRoute title="My account"><ProfilePage /></NoIndexRoute>} />
                  </Route>

                  {/* ── Admin protected routes ── */}
                  <Route element={<ProtectedRoute adminOnly />}>
                    <Route path="/admin"           element={<NoIndexRoute title="Admin dashboard"><AdminDashboard /></NoIndexRoute>} />
                    <Route path="/admin/products"  element={<NoIndexRoute title="Manage products"><AdminProducts /></NoIndexRoute>} />
                    <Route path="/admin/orders"    element={<NoIndexRoute title="Manage orders"><AdminOrders /></NoIndexRoute>} />
                    <Route path="/admin/reports"   element={<NoIndexRoute title="Sales reports"><AdminReports /></NoIndexRoute>} />
                    <Route path="/admin/demos"     element={<NoIndexRoute title="Demo requests"><AdminDemos /></NoIndexRoute>} />
                    <Route path="/admin/exchange"  element={<NoIndexRoute title="Exchange requests"><AdminExchange /></NoIndexRoute>} />
                    <Route path="/admin/coupons"   element={<NoIndexRoute title="Coupons"><AdminCoupons /></NoIndexRoute>} />
                    <Route path="/admin/prebookings" element={<NoIndexRoute title="Pre-bookings"><AdminPrebookings /></NoIndexRoute>} />
                    <Route path="/admin/billing"   element={<NoIndexRoute title="Billing & invoices"><AdminBilling /></NoIndexRoute>} />
                    <Route path="/admin/customers" element={<NoIndexRoute title="Manage customers"><AdminCustomers /></NoIndexRoute>} />
                  </Route>

                  <Route path="/404" element={<NoIndexRoute title="Page not found"><NotFound /></NoIndexRoute>} />
                  <Route path="*"    element={<Navigate to="/404" replace />} />
                </Route>

                {/* Auth screens intentionally use a focused, distraction-free layout. */}
                <Route path="/login"          element={<NoIndexRoute title="Sign in"><LoginPage /></NoIndexRoute>} />
                <Route path="/register"       element={<NoIndexRoute title="Create account"><RegisterPage /></NoIndexRoute>} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>

      {/* React Query Devtools — visible in development only */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
